// Shared runtime pieces for the mech kits: the Kit interface every mech
// implements, a gameplay-time scheduler (freezes in hitstop and pause, follows
// slow motion), fallback textures, a crash-proof facade over other systems that
// may still be stubs, and an additive lightning renderer for the big moments.

import Phaser from 'phaser';
import { DEPTH } from '../../config/constants';
import type { EnemyApi, GameContext, Vec } from '../../core/contracts';
import type { DamageInfo, SfxId } from '../../core/types';

// ───────────────────────────── Kit contract ─────────────────────────────

/** One mech's passive + signature + ultimate. Created by createAbilities; never touches other systems before start(). */
export interface Kit {
  readonly signatureActive: boolean;
  readonly ultimateActive: boolean;
  /** Every system exists now: subscribe to the bus here. */
  start(): void;
  /** False when the signature cannot fire right now (no cooldown is consumed). */
  canSignature(): boolean;
  signature(): void;
  ultimate(): void;
  /** Passive / ability-state multiplier on outgoing player damage. */
  outgoing(target: EnemyApi | null, info: DamageInfo): number;
  /** Passive / ability-state multiplier on incoming damage. */
  incoming(info: DamageInfo): number;
  update(dt: number): void;
  /** The player died / the room is ending: stop active effects (restore buffs, colours, projectiles). */
  interrupt(): void;
  destroy(): void;
}

/** Services the core hands to each kit. */
export interface KitHost {
  readonly ctx: GameContext;
  readonly timers: Scheduler;
  readonly safe: Safe;
  readonly bolts: Bolts;
  /** Behaviour flag from mutations / synergies / upgrades (false while effects is a stub). */
  flag(name: string): boolean;
  /** Seconds the player has been still on the ground. */
  readonly stillFor: number;
  /** Subscribe to the bus; dropped in destroy(). */
  on: GameContext['bus']['on'];
  /** Register a game object so destroy() removes it. */
  track<T extends Phaser.GameObjects.GameObject>(obj: T): T;
  untrack(obj: Phaser.GameObjects.GameObject): void;
}

// ───────────────────────────── Scheduler ─────────────────────────────

export interface TimerHandle {
  cancel(): void;
  readonly done: boolean;
}

interface Task {
  at: number;
  interval: number;
  remaining: number; // repeats left (Infinity for until-cancelled)
  index: number;
  fn: (index: number) => void;
  done: boolean;
}

/** Gameplay-time timers driven by the abilities update (so hitstop / pause freeze them). */
export class Scheduler {
  private t = 0;
  private tasks: Task[] = [];

  get now(): number {
    return this.t;
  }

  after(seconds: number, fn: () => void): TimerHandle {
    return this.push({ at: this.t + Math.max(0, seconds), interval: 0, remaining: 1, index: 0, fn: () => fn(), done: false });
  }

  /** Call fn(i) every `interval` s, `count` times; the first call after `delay` (default: immediately on the next update). */
  every(interval: number, count: number, fn: (index: number) => void, delay = 0): TimerHandle {
    return this.push({ at: this.t + Math.max(0, delay), interval: Math.max(0.01, interval), remaining: count, index: 0, fn, done: false });
  }

  update(dt: number): void {
    this.t += dt;
    if (this.tasks.length === 0) return;
    // Tasks may schedule new tasks while running: iterate over a snapshot.
    const list = this.tasks.slice();
    for (const task of list) {
      while (!task.done && task.at <= this.t) {
        const i = task.index++;
        task.remaining--;
        if (task.remaining <= 0) task.done = true;
        else task.at += task.interval;
        try {
          task.fn(i);
        } catch (err) {
          console.error('[abilities] timer callback failed', err);
        }
      }
    }
    this.tasks = this.tasks.filter((t) => !t.done);
  }

  clear(): void {
    for (const t of this.tasks) t.done = true;
    this.tasks = [];
  }

  private push(task: Task): TimerHandle {
    this.tasks.push(task);
    return {
      cancel: () => {
        task.done = true;
      },
      get done() {
        return task.done;
      },
    };
  }
}

// ───────────────────────────── Safe facade ─────────────────────────────

/** Calls into other systems that log (once per label) instead of crashing the frame while they are stubs. */
export class Safe {
  private warned = new Set<string>();

  call<T>(label: string, fn: () => T, fallback: T): T {
    try {
      return fn();
    } catch (err) {
      if (!this.warned.has(label)) {
        this.warned.add(label);
        console.warn(`[abilities] ${label} unavailable:`, (err as Error)?.message ?? err);
      }
      return fallback;
    }
  }

  run(label: string, fn: () => void): void {
    this.call(label, fn, undefined);
  }
}

export function sfx(ctx: GameContext, safe: Safe, id: SfxId, opts?: { volume?: number; pitch?: number }): void {
  safe.run(`audio.${id}`, () => ctx.audio.play(id, opts));
}

// ───────────────────────────── Textures ─────────────────────────────

/** Fallback textures generated by abilities (used when the shared FX art is missing). */
export const ATEX = {
  soft: 'abil_soft',
  hex: 'abil_hex',
  bubble: 'abil_bubble',
  diamond: 'abil_diamond',
  vignette: 'abil_vignette',
  streak: 'abil_streak',
  ring: 'abil_ring',
} as const;

/** Create the fallback textures once per game (the texture manager is global). */
export function ensureAbilityTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  const canvas = (key: string, w: number, h: number, draw: (c: CanvasRenderingContext2D) => void): void => {
    if (tm.exists(key)) return;
    const tex = tm.createCanvas(key, w, h);
    if (!tex) return;
    draw(tex.context);
    tex.refresh();
  };
  canvas(ATEX.soft, 64, 64, (c) => {
    const g = c.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.35, 'rgba(255,255,255,0.55)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, 64, 64);
  });
  canvas(ATEX.hex, 32, 32, (c) => {
    // One hex cell that tiles as a honeycomb (offset rows handled by the 32 px tile).
    c.strokeStyle = 'rgba(255,255,255,0.9)';
    c.lineWidth = 1.5;
    hexPath(c, 16, 10, 9);
    c.stroke();
    c.strokeStyle = 'rgba(255,255,255,0.45)';
    hexPath(c, 0, 26, 9);
    c.stroke();
    hexPath(c, 32, 26, 9);
    c.stroke();
  });
  canvas(ATEX.bubble, 128, 128, (c) => {
    const g = c.createRadialGradient(64, 64, 30, 64, 64, 62);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.8, 'rgba(255,255,255,0.25)');
    g.addColorStop(0.97, 'rgba(255,255,255,0.9)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.beginPath();
    c.arc(64, 64, 63, 0, Math.PI * 2);
    c.fill();
    c.save();
    c.beginPath();
    c.arc(64, 64, 60, 0, Math.PI * 2);
    c.clip();
    c.strokeStyle = 'rgba(255,255,255,0.22)';
    c.lineWidth = 1;
    for (let row = -1; row < 9; row++) {
      for (let col = -1; col < 9; col++) {
        hexPath(c, col * 16 + (row % 2 ? 8 : 0), row * 14, 8);
        c.stroke();
      }
    }
    c.restore();
  });
  canvas(ATEX.diamond, 28, 28, (c) => {
    c.fillStyle = 'rgba(255,255,255,0.35)';
    c.beginPath();
    c.moveTo(14, 0);
    c.lineTo(28, 14);
    c.lineTo(14, 28);
    c.lineTo(0, 14);
    c.closePath();
    c.fill();
    c.strokeStyle = '#ffffff';
    c.lineWidth = 2.5;
    c.beginPath();
    c.moveTo(14, 3);
    c.lineTo(25, 14);
    c.lineTo(14, 25);
    c.lineTo(3, 14);
    c.closePath();
    c.stroke();
  });
  canvas(ATEX.vignette, 320, 180, (c) => {
    const g = c.createRadialGradient(160, 90, 40, 160, 90, 190);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.55, 'rgba(255,255,255,0.12)');
    g.addColorStop(1, 'rgba(255,255,255,1)');
    c.fillStyle = g;
    c.fillRect(0, 0, 320, 180);
  });
  canvas(ATEX.streak, 64, 8, (c) => {
    const g = c.createLinearGradient(0, 0, 64, 0);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.7, 'rgba(255,255,255,0.8)');
    g.addColorStop(1, 'rgba(255,255,255,1)');
    c.fillStyle = g;
    c.fillRect(0, 2, 64, 4);
  });
  canvas(ATEX.ring, 128, 128, (c) => {
    c.strokeStyle = '#ffffff';
    c.lineWidth = 4;
    c.beginPath();
    c.arc(64, 64, 60, 0, Math.PI * 2);
    c.stroke();
  });
}

function hexPath(c: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  c.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = Math.PI / 6 + (i * Math.PI) / 3;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) c.moveTo(x, y);
    else c.lineTo(x, y);
  }
  c.closePath();
}

/** Shared texture key if the art exists, else our generated fallback. */
export function texOr(scene: Phaser.Scene, shared: string, fallback: string): string {
  return scene.textures.exists(shared) ? shared : fallback;
}

// ───────────────────────────── Enemy queries ─────────────────────────────

/** Enemies that abilities may target: alive, active, not breakable props. */
export function targetable(e: EnemyApi): boolean {
  return e.alive && !e.spawning && e.kind !== 'prop';
}

export function targetableList(ctx: GameContext, safe: Safe): EnemyApi[] {
  const list = safe.call('enemies.list', () => ctx.enemies.list, [] as readonly EnemyApi[]);
  const out: EnemyApi[] = [];
  for (const e of list) if (targetable(e)) out.push(e);
  return out;
}

/** Current camera view in world space (with an optional margin). */
export function cameraView(ctx: GameContext, margin = 0): Phaser.Geom.Rectangle {
  const v = ctx.scene.cameras.main.worldView;
  return new Phaser.Geom.Rectangle(v.x - margin, v.y - margin, v.width + margin * 2, v.height + margin * 2);
}

export function dist(a: Vec, b: Vec): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// ───────────────────────────── Lightning renderer ─────────────────────────────

interface Bolt {
  g: Phaser.GameObjects.Graphics;
  points: Vec[];
  color: number;
  width: number;
  life: number;
  age: number;
  jitter: number;
  branches: boolean;
  /** Time until the next crackle re-draw. */
  next: number;
}

/**
 * Additive, crackling lightning owned by abilities (the spectacle layer on top of
 * combat's own chain FX). Each bolt redraws ~25×/s with fresh jitter while it
 * fades: wide soft glow, coloured body, white-hot core and short side branches.
 */
export class Bolts {
  private bolts: Bolt[] = [];
  private pool: Phaser.GameObjects.Graphics[] = [];
  private dead = false;

  constructor(private readonly scene: Phaser.Scene) {}

  strike(points: readonly Vec[], opts: { color: number; width?: number; life?: number; jitter?: number; branches?: boolean }): void {
    if (this.dead || points.length < 2) return;
    let g = this.pool.pop();
    if (!g || !g.scene) {
      g = this.scene.add.graphics();
      g.setBlendMode(Phaser.BlendModes.ADD);
    }
    g.setDepth(DEPTH.FX_ADD).setVisible(true).setAlpha(1);
    const bolt: Bolt = {
      g,
      points: points.map((p) => ({ x: p.x, y: p.y })),
      color: opts.color,
      width: opts.width ?? 4,
      life: opts.life ?? 0.28,
      age: 0,
      jitter: opts.jitter ?? 18,
      branches: opts.branches ?? true,
      next: 0,
    };
    this.bolts.push(bolt);
    this.draw(bolt);
  }

  update(dt: number): void {
    if (this.bolts.length === 0) return;
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i];
      b.age += dt;
      if (b.age >= b.life || !b.g.scene) {
        this.bolts.splice(i, 1);
        if (b.g.scene) {
          b.g.clear().setVisible(false);
          this.pool.push(b.g);
        }
        continue;
      }
      b.next -= dt;
      if (b.next <= 0) this.draw(b);
      else b.g.setAlpha(1 - (b.age / b.life) ** 2);
    }
  }

  private draw(b: Bolt): void {
    b.next = 0.04;
    const g = b.g;
    const fade = 1 - (b.age / b.life) ** 2;
    g.clear();
    g.setAlpha(fade);
    const path: Vec[] = [];
    for (let i = 0; i < b.points.length - 1; i++) {
      const a = b.points[i];
      const c = b.points[i + 1];
      const len = Math.hypot(c.x - a.x, c.y - a.y);
      const steps = Math.max(2, Math.round(len / 22));
      const nx = -(c.y - a.y) / (len || 1);
      const ny = (c.x - a.x) / (len || 1);
      for (let s = 0; s < steps; s++) {
        const t = s / steps;
        // Zero offset at the endpoints so the bolt always meets its targets.
        const amp = s === 0 ? 0 : (Math.random() - 0.5) * 2 * b.jitter * Math.sin(Math.PI * t);
        path.push({ x: a.x + (c.x - a.x) * t + nx * amp, y: a.y + (c.y - a.y) * t + ny * amp });
      }
    }
    path.push(b.points[b.points.length - 1]);

    const stroke = (w: number, color: number, alpha: number): void => {
      g.lineStyle(w, color, alpha);
      g.beginPath();
      g.moveTo(path[0].x, path[0].y);
      for (let i = 1; i < path.length; i++) g.lineTo(path[i].x, path[i].y);
      g.strokePath();
    };
    stroke(b.width * 4.5, b.color, 0.12);
    stroke(b.width * 2, b.color, 0.45);
    stroke(b.width, b.color, 0.95);
    stroke(Math.max(1, b.width * 0.4), 0xffffff, 1);

    if (b.branches && path.length > 3) {
      g.lineStyle(Math.max(1, b.width * 0.45), b.color, 0.8);
      for (let i = 2; i < path.length - 1; i += 3) {
        if (Math.random() < 0.45) continue;
        const p = path[i];
        let x = p.x;
        let y = p.y;
        const ang = Math.random() * Math.PI * 2;
        g.beginPath();
        g.moveTo(x, y);
        for (let k = 0; k < 3; k++) {
          x += Math.cos(ang + (Math.random() - 0.5) * 1.4) * (8 + Math.random() * 12);
          y += Math.sin(ang + (Math.random() - 0.5) * 1.4) * (8 + Math.random() * 12);
          g.lineTo(x, y);
        }
        g.strokePath();
      }
    }
  }

  destroy(): void {
    this.dead = true;
    for (const b of this.bolts) b.g.destroy();
    for (const g of this.pool) g.destroy();
    this.bolts = [];
    this.pool = [];
  }
}
